import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNavigationContext } from "../../../common/navigation/NavigationContext";
import S3MonitorView from "../../../upload/components/S3Monitor/S3MonitorView";
import WorkflowValidationView from "./Workflow_Validation_View";

// 🎯 FIXED: Logic is encapsulated in DiscoveryFactory
import { DiscoveryFactory } from "../../../common/parsers/DiscoveryFactory";
import DiscoveryProgressBar from "../../../download/DiscoveryProgressBar";

// 🎯 FIXED: Centralized handshake helper
import { executeWorkflowHandshake } from "./Workflow_XLS_Support";

// 🕵️ NEW: Auditor to generate the summary for the Trawler View
import { TaxonomyTrawlerController } from "./controllers/TaxonomyTrawlerController";

// 🛠️ IMPORT FOR DIAGNOSTIC TEST & DTO
import { SaveESG_taxonomyController } from "./controllers/SaveESG_taxonomyController";
import { GenericFileUploader } from "../../../upload/services/genericFileUploader";
import { useBatch } from "../../../upload/context/BatchContext";

export default function WorkflowHub() {
  const navigate = useNavigate();
  const { activeWorkflow, setWorkflowContext } = useNavigationContext();
  const { refreshAllBatches } = useBatch(); // 🎯 Connects to the Batch Monitoring State

  // 📦 Hub State
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationResults, setValidationResults] = useState(null);

  // 🎯 Progress State for the ProgressBar
  const [currentProgress, setCurrentProgress] = useState({
    stage: "IDLE",
    status: "IDLE",
    percent: 0,
    label: "",
  });

  useEffect(() => {
    setWorkflowContext(
      "Refresh ESG Taxonomy",
      "/registered/site-admin/taxonomy-workflow",
    );
  }, [setWorkflowContext]);

  // ===========================================================================
  // 🧪 DEBUG BLOCK: START (REMOVE THIS SECTION ONCE CORS/BFF IS VERIFIED)
  // ===========================================================================
  const handleDiagnosticTest = async () => {
    console.warn("🧪 [DIAGNOSTIC] Initiating Headless ESG Sync...");

    try {
      // 1. RESOLVE THE HANDSHAKE KEY
      // Pull from ENV or Fallback to the context you are already using in the Hub
      const siteKey = process.env.REACT_APP_SITE_KEY || activeWorkflow?.siteKey;

      if (!siteKey) {
        console.error(
          "❌ [DIAGNOSTIC_ABORT]: No siteKey found. Handshake will fail.",
        );
        return;
      }

      // 2. EXECUTE THE CONTROLLER (Controller creates its own dummy data)
      const result =
        await SaveESG_taxonomyController.promoteToCleanRoute(siteKey);

      // 3. REFRESH THE UI
      if (result?.success) {
        await refreshAllBatches();
        console.log(`✅ Success: Batch ${result.batchId} is now in the queue.`);
      }
    } catch (err) {
      console.error("❌ [DIAGNOSTIC_FAIL]:", err);
    }
  };
  // ===========================================================================
  // 🧪 DEBUG BLOCK: END
  // ===========================================================================

  /**
   * 🕵️ LOCAL DISCOVERY LAUNCH
   */
  const handleStartValidation = async () => {
    if (!selectedFile) {
      console.error("❌ [EVIDENCE: HUB] Cannot start: selectedFile is NULL.");
      return;
    }

    setIsProcessing(true);
    setValidationResults(null);

    setCurrentProgress({
      stage: "DOWNLOAD",
      status: "IN_PROGRESS",
      percent: 5,
      label: "Requesting Secure S3 Access...",
    });

    try {
      const payload = {
        siteUserGuid: sessionStorage.getItem("SEC_USER_GUID") || "SYSTEM_USER",
        bucketAlias: process.env.REACT_APP_SITE_DIRTY_BUCKET,
        fileName: selectedFile.path,
      };

      const authData = await executeWorkflowHandshake(
        "doorman/taxonomy",
        payload,
        "Download",
      );

      const actualResult = authData.data || authData;

      if (actualResult.status !== "SIGNATURE_GENERATED") {
        throw new Error(actualResult.message || "S3 Signature Refused");
      }

      const localData = await DiscoveryFactory.run(
        actualResult.url,
        selectedFile.filename,
        (progress) => setCurrentProgress(progress),
      );

      const summary = TaxonomyTrawlerController.generateAuditSummary(
        localData.xbrlRelationships,
        localData.esgTree,
      );
      const physicalStats = TaxonomyTrawlerController.generatePhysicalStats(
        localData.xbrlRelationships,
      );

      setValidationResults({
        ...localData,
        summary,
        physicalStats,
      });

      setCurrentProgress({
        stage: "COMPLETE",
        status: "SUCCESS",
        percent: 100,
        label: "Discovery Complete",
      });
    } catch (err) {
      console.error("❌ [EVIDENCE: HUB] Local Discovery Failed:", err);
      setCurrentProgress({
        stage: "DOWNLOAD",
        status: "FAIL",
        percent: 100,
        label: err.message,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="page-main mt-5 pt-5" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section px-4">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xl-8">
                <div className="d-flex justify-content-between align-items-end mb-4 border-bottom pb-3">
                  <h2 className="h4 fw-bold mb-1 text-dark">Workflow Hub</h2>
                  <div className="text-end text-muted extra-small text-uppercase fw-bold">
                    Mode: Local Discovery
                  </div>
                </div>

                {/* STEP 1: MONITOR */}
                <div className="card shadow-sm border-0 bg-white overflow-hidden mb-4">
                  <div className="card-header bg-white py-3 border-bottom">
                    <h6 className="mb-0 fw-bold text-primary">
                      1. Physical Inventory Discovery
                    </h6>
                  </div>
                  <S3MonitorView
                    targetBucket={process.env.REACT_APP_SITE_DIRTY_BUCKET}
                    config={{ siteKey: process.env.REACT_APP_SITE_KEY }}
                    onFileSelect={(file) => {
                      setSelectedFile(file);
                      setValidationResults(null);
                      setCurrentProgress({
                        stage: "IDLE",
                        percent: 0,
                        label: "Ready",
                      });
                    }}
                  />
                </div>

                {isProcessing && (
                  <div className="mb-4 animate__animated animate__fadeIn">
                    <DiscoveryProgressBar currentProgress={currentProgress} />
                  </div>
                )}

                {/* STEP 2: VALIDATOR */}
                {!isProcessing && (
                  <div className="mb-4">
                    <WorkflowValidationView
                      selectedFile={selectedFile}
                      results={validationResults}
                      isProcessing={isProcessing}
                      onValidate={handleStartValidation}
                      onProceed={() =>
                        navigate(
                          "/registered/site-admin/taxonomy-workflow/cycle-mapping",
                        )
                      }
                    />
                  </div>
                )}

                {/* FOOTER */}
                <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none p-0 text-secondary small d-flex align-items-center"
                    onClick={() => navigate(activeWorkflow?.path || -1)}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Return to {activeWorkflow?.name || "Previous Page"}
                  </button>

                  {/* 🧪 REMOVE: TEST SAVE BUTTON SECTION */}
                  <button
                    className="btn btn-danger btn-sm fw-bold shadow-sm animate__animated animate__pulse animate__infinite"
                    onClick={handleDiagnosticTest}
                  >
                    <i className="bi bi-bug-fill me-1"></i> RUN DIAGNOSTIC SAVE
                  </button>
                  {/* 🧪 END REMOVE */}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
